import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
// Emit the worker as its own static file so it can run in a real Web Worker.
// Other modules import "pdf.worker.entry", which puts the worker on
// window.pdfjsWorker and makes pdf.js parse every PDF on the main thread.
// eslint-disable-next-line import/no-webpack-loader-syntax
import pdfWorkerUrl from "!!file-loader?name=static/js/[name].[hash:8].[ext]!pdfjs-dist/legacy/build/pdf.worker.min.js";

const MAX_CACHED_DOCS = 8;
const RANGE_CHUNK = 262144;
const RANGE_RETRY_DELAYS = [300, 1000, 2500, 5000];

export const PDF_LOAD_OPTIONS = {
  isEvalSupported: false,
  disableAutoFetch: true,
  disableStream: false,
  disableRange: false,
  rangeChunkSize: RANGE_CHUNK,
};

export const isPdfUrl = (url = "") =>
  typeof url === "string" && url.split("?")[0].toLowerCase().endsWith(".pdf");

let sharedWorker = null;
let workerBroken = false;

function handleWorkerFailure() {
  if (workerBroken) return;
  workerBroken = true;
  sharedWorker = null;
  cache.forEach((entry, url) => {
    if (!entry.settled && entry.usedWorker) startAttempt(url, entry);
  });
}

export function getSharedPdfWorker() {
  if (workerBroken) return null;
  if (sharedWorker && !sharedWorker.destroyed) return sharedWorker;
  if (typeof window === "undefined" || typeof Worker === "undefined") {
    return null;
  }
  try {
    const port = new Worker(pdfWorkerUrl);
    port.addEventListener("error", handleWorkerFailure);
    sharedWorker = new PDFJS.PDFWorker({ name: "doc-viewer", port });
  } catch (err) {
    workerBroken = true;
    sharedWorker = null;
  }
  return sharedWorker;
}

const cache = new Map(); // url -> entry

function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

const canUseRangeTransport = () =>
  typeof fetch === "function" &&
  typeof AbortController === "function" &&
  typeof ReadableStream === "function" &&
  typeof PDFJS.PDFDataRangeTransport === "function";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function concatBytes(parts, total) {
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.byteLength;
  }
  return out;
}

function createRangeTransport(url, length, initialData, onFatal) {
  const transport = new PDFJS.PDFDataRangeTransport(length, initialData);
  const controllers = new Set();
  let aborted = false;
  let wholeFile = null; // set if the server ignores Range and sends it all

  const fetchRange = async (begin, end) => {
    for (let attempt = 0; ; attempt++) {
      if (aborted) throw new Error("aborted");
      if (wholeFile) return wholeFile.slice(begin, end);
      const controller = new AbortController();
      controllers.add(controller);
      try {
        const res = await fetch(url, {
          headers: { Range: `bytes=${begin}-${end - 1}` },
          signal: controller.signal,
        });
        if (res.status === 206) return new Uint8Array(await res.arrayBuffer());
        if (res.status === 200) {
          wholeFile = new Uint8Array(await res.arrayBuffer());
          return wholeFile.slice(begin, end);
        }
        throw new Error(`HTTP ${res.status}`);
      } catch (err) {
        if (aborted || attempt >= RANGE_RETRY_DELAYS.length) throw err;
        await wait(RANGE_RETRY_DELAYS[attempt]);
      } finally {
        controllers.delete(controller);
      }
    }
  };

  transport.requestDataRange = (begin, end) => {
    fetchRange(begin, end).then(
      (chunk) => {
        if (!aborted) transport.onDataRange(begin, chunk);
      },
      (err) => {
        if (!aborted) onFatal(err);
      },
    );
  };
  transport.abort = () => {
    aborted = true;
    controllers.forEach((c) => c.abort());
    controllers.clear();
  };
  return transport;
}

async function openSource(url, entry, onFatal) {
  if (!canUseRangeTransport()) return { url };
  const controller = new AbortController();
  entry.abortProbe = () => controller.abort();
  try {
    const res = await fetch(url, { signal: controller.signal });
    const length = parseInt(res.headers.get("Content-Length"), 10);
    if (!res.ok || !res.body || !Number.isFinite(length)) {
      controller.abort();
      return { url };
    }
    if (length <= 2 * RANGE_CHUNK) {
      return { data: new Uint8Array(await res.arrayBuffer()) };
    }
    const reader = res.body.getReader();
    const parts = [];
    let received = 0;
    while (received < RANGE_CHUNK) {
      const { value, done } = await reader.read();
      if (done) break;
      parts.push(value);
      received += value.byteLength;
    }
    reader.cancel().catch(() => {});
    controller.abort();
    const initialData = concatBytes(parts, received);
    if (received >= length) return { data: initialData };
    return {
      range: createRangeTransport(url, length, initialData, onFatal),
    };
  } catch (err) {
    if (controller.signal.aborted && entry.closed) throw err;
    return { url };
  } finally {
    entry.abortProbe = null;
  }
}

function documentParams(source, worker) {
  return {
    ...PDF_LOAD_OPTIONS,
    ...(source.range ? { range: source.range } : {}),
    ...(source.data ? { data: source.data } : {}),
    ...(source.url ? { url: source.url } : {}),
    ...(worker ? { worker } : {}),
  };
}

export function openPdfOnce(url) {
  const probe = { closed: false, abortProbe: null };
  let task = null;
  const promise = openSource(url, probe, () => {
    if (task) task.destroy();
  }).then((source) => {
    if (probe.closed) {
      if (source.range) source.range.abort();
      throw new Error("PDF load cancelled");
    }
    task = PDFJS.getDocument(documentParams(source, getSharedPdfWorker()));
    return task.promise;
  });
  promise.catch(() => undefined);
  return {
    promise,
    destroy: () => {
      probe.closed = true;
      if (probe.abortProbe) probe.abortProbe();
      if (task) task.destroy();
    },
  };
}

function failEntry(url, entry, err) {
  if (cache.get(url) === entry) cache.delete(url);
  entry.closed = true;
  if (!entry.settled) {
    entry.settled = true;
    entry.deferred.reject(err);
  }
  entry.failListeners.forEach((fn) => fn(err));
  entry.failListeners.clear();
  try {
    if (entry.task) entry.task.destroy();
  } catch (e) {}
}

function startAttempt(url, entry) {
  if (entry.task) {
    try {
      entry.task.destroy();
    } catch (err) {}
    entry.task = null;
  }
  const attempt = ++entry.attempt;
  const isCurrent = () => entry.attempt === attempt && !entry.closed;
  const onFatal = (err) => {
    if (isCurrent()) failEntry(url, entry, err);
  };

  openSource(url, entry, onFatal).then(
    (source) => {
      if (!isCurrent()) {
        if (source.range) source.range.abort();
        return;
      }
      const worker = getSharedPdfWorker();
      entry.usedWorker = !!worker;
      const task = PDFJS.getDocument(documentParams(source, worker));
      entry.task = task;
      task.promise.then(
        (pdf) => {
          if (entry.task !== task) return;
          entry.settled = true;
          entry.pdf = pdf;
          entry.deferred.resolve(pdf);
        },
        (err) => {
          if (entry.task !== task) return;
          failEntry(url, entry, err);
        },
      );
    },
    (err) => {
      if (isCurrent()) failEntry(url, entry, err);
    },
  );
}

function evictIfNeeded() {
  if (cache.size <= MAX_CACHED_DOCS) return;
  const idle = [...cache.entries()]
    .filter(([, entry]) => entry.refs === 0)
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
  while (cache.size > MAX_CACHED_DOCS && idle.length) {
    const [url, entry] = idle.shift();
    cache.delete(url);
    entry.closed = true;
    if (entry.abortProbe) entry.abortProbe();
    try {
      if (entry.task) entry.task.destroy();
    } catch (err) {}
  }
}

function getEntry(url) {
  let entry = cache.get(url);
  if (!entry) {
    entry = {
      deferred: createDeferred(),
      task: null,
      pdf: null,
      refs: 0,
      settled: false,
      closed: false,
      attempt: 0,
      abortProbe: null,
      failListeners: new Set(),
      usedWorker: false,
      lastUsed: Date.now(),
    };
    cache.set(url, entry);
    startAttempt(url, entry);
    evictIfNeeded();
  }
  entry.lastUsed = Date.now();
  return entry;
}

export function getCachedPdf(url) {
  const entry = url ? cache.get(url) : null;
  return entry?.pdf || null;
}

export function acquirePdf(url) {
  const entry = getEntry(url);
  entry.refs += 1;
  let released = false;
  const listeners = new Set();
  return {
    promise: entry.deferred.promise,
    onFail: (fn) => {
      listeners.add(fn);
      entry.failListeners.add(fn);
    },
    release: () => {
      if (released) return;
      released = true;
      listeners.forEach((fn) => entry.failListeners.delete(fn));
      entry.refs = Math.max(0, entry.refs - 1);
      entry.lastUsed = Date.now();
      evictIfNeeded();
    },
  };
}

export function prefetchPdf(url) {
  if (!url || !isPdfUrl(url)) return;
  const entry = getEntry(url);
  entry.deferred.promise.then((pdf) => pdf.getPage(1)).catch(() => {});
}

const hoverTimers = new Map();

export function schedulePrefetch(url, delay = 120) {
  if (!url || !isPdfUrl(url) || cache.has(url) || hoverTimers.has(url)) return;
  hoverTimers.set(
    url,
    setTimeout(() => {
      hoverTimers.delete(url);
      prefetchPdf(url);
    }, delay),
  );
}

export function cancelScheduledPrefetch(url) {
  const timer = hoverTimers.get(url);
  if (timer) {
    clearTimeout(timer);
    hoverTimers.delete(url);
  }
}

export const prefetchHandlers = (url) => ({
  onMouseEnter: () => schedulePrefetch(url),
  onMouseLeave: () => cancelScheduledPrefetch(url),
  onFocus: () => schedulePrefetch(url, 0),
  onTouchStart: () => prefetchPdf(url),
});
